import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCart } from "../context/CartContext";
import { getCsrfToken } from "../services/authService";
import { useAuth } from "../hooks/useAuth";
import "../styles/ProductDetail.css";

export default function ProductDetail() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const { addToCart } = useCart();
  const navigate = useNavigate();

  const location = useLocation();
  const cameFromFavorites = location.state?.fromFavorites;

  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState("");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [quantityError, setQuantityError] = useState("");

  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [reviewError, setReviewError] = useState("");
  const [reviewSuccess, setReviewSuccess] = useState("");

  const [favorite, setFavorite] = useState(false);

  const [relatedProducts, setRelatedProducts] = useState([])

  const { authenticated } = useAuth();

  useEffect(() => {
    loadProduct();
  }, [slug]);

  useEffect(() => {
    if (!authenticated || !product) return;

    const pendingFavoriteProductId = sessionStorage.getItem(
      "pendingFavoriteProductId"
    );

    if (!pendingFavoriteProductId) return;

    if (Number(pendingFavoriteProductId) !== Number(product.id)) return;

    sessionStorage.removeItem("pendingFavoriteProductId");

    toggleFavorite();
  }, [authenticated, product]);

  const loadFavorite = async (productId) => {
    const response = await fetch("/api/favorites", {
      credentials: "include",
    });

    if (response.ok) {
      const data = await response.json();

      setFavorite(
        data.some((favorite) => Number(favorite.productId) === Number(productId))
      );
    }
  };

  const saveRecentlyViewed = (productData) => {
    const productToSave = {
      id: productData.id,
      name: productData.name,
      slug: productData.slug,
      imageUrl: productData.imageUrl,
      price: productData.price,
      averageRating: productData.averageRating,
      reviewsCount: productData.reviewsCount,
    };

    const current = JSON.parse(
      localStorage.getItem("recentlyViewedProducts") || "[]"
    );

    const filtered = current.filter(
      (item) => Number(item.id) !== Number(productData.id)
    );

    const updated = [productToSave, ...filtered].slice(0, 6);

    localStorage.setItem(
      "recentlyViewedProducts",
      JSON.stringify(updated)
    );
  };

  const loadProduct = async () => {
    const response = await fetch(`/api/products/${slug}`, {
      credentials: "include",
    });

    if (response.ok) {
      const data = await response.json();
      setProduct(data);
      saveRecentlyViewed(data);
      setSelectedImage(data.imageUrl || data.imageUrls?.[0] || "");
      setQuantity(1);

      if (authenticated) {
        await loadFavorite(data.id);
      }

      await loadReviews(data.id);
      await loadRelatedProducts(data.id);
    }
  };

  const loadRelatedProducts = async (productId) => {
    const response = await fetch(`/api/products/${productId}/related`, {
      credentials: "include",
    });

    if (response.ok) {
      setRelatedProducts(await response.json());
    }
  };

  const loadReviews = async (productId) => {
    const response = await fetch(`/api/products/${productId}/reviews`, {
      credentials: "include",
    });

    if (response.ok) {
      setReviews(await response.json());
    }
  };

  const submitReview = async () => {
    setReviewError("");
    setReviewSuccess("");

    try {
      const csrf = await getCsrfToken();

      const response = await fetch(`/api/products/${product.id}/reviews`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          [csrf.headerName]: csrf.token,
        },
        body: JSON.stringify({
          rating,
          comment,
        }),
      });

      if (!response.ok) {
        setReviewError(t("productDetail.reviewError"));
        return;
      }

      setComment("");
      setRating(5);
      setReviewSuccess(t("productDetail.reviewSuccess"));
      await loadReviews(product.id);
      await loadProduct();
    } catch {
      setReviewError(t("productDetail.reviewError"));
    }
  };

  if (!product) return null;

  const handleQuantityChange = (e) => {
    const value = Number(e.target.value);

    setQuantityError("");

    if (!value || value < 1) {
      setQuantity(1);
      return;
    }

    if (value > product.stock) {
      setQuantity(product.stock);
      setQuantityError(t("productDetail.errorMaxQuantity"));
      return;
    }

    setQuantity(value);
  };

  const handleAddToCart = () => {
    if (product.stock <= 0) {
      return;
    }

    if (quantity < 1) {
      setQuantityError(t("productDetail.errorMinQuantity"));
      return;
    }

    if (quantity > product.stock) {
      setQuantityError(t("productDetail.errorMaxQuantity"));
      return;
    }

    addToCart(product, quantity);
  };

  const toggleFavorite = async () => {
    if (!authenticated) {
      sessionStorage.setItem(
        "pendingFavoriteProductId",
        String(product.id)
      );

      navigate("/login", {
        state: { redirect: `/shop/${product.slug}` },
      });

      return;
    }

    const csrf = await getCsrfToken();

    const response = await fetch(`/api/favorites/${product.id}`, {
      method: favorite ? "DELETE" : "POST",
      credentials: "include",
      headers: {
        [csrf.headerName]: csrf.token,
      },
    });

    if (response.ok) {
      await loadFavorite(product.id);
    }
  };

  return (
    <main className="product-detail-page">
      <button
        type="button"
        className="back-to-shop-btn"
        onClick={() => navigate(cameFromFavorites ? "/favorites" : "/shop")}
      >
        ← {cameFromFavorites
          ? t("productDetail.backToFavorites")
          : t("productDetail.backToShop")}
      </button>
      <section className="product-detail-container">
        <div className="product-gallery">
          {selectedImage && (
            <img
              className="product-main-image"
              src={selectedImage}
              alt={product.name}
              onClick={() => setLightboxOpen(true)}
            />
          )}

          <div className="product-thumbnails">
            {product.imageUrls?.map((image) => (
              <img
                key={image}
                src={image}
                alt={product.name}
                className={selectedImage === image ? "selected" : ""}
                onClick={() => setSelectedImage(image)}
              />
            ))}
          </div>
        </div>

        <div className="product-info-detail">
          <p className="product-brand">{product.brand}</p>
          <h1>{product.name}</h1>

          <button
            type="button"
            className={`product-favorite-btn ${favorite ? "active" : ""}`}
            onClick={toggleFavorite}
          >
            ♥ {favorite ? t("productDetail.removeFavorite") : t("productDetail.addFavorite")}
          </button>

          <div className="product-meta-detail">
            {product.sku && (
              <span>
                {t("productDetail.sku")}: <strong>{product.sku}</strong>
              </span>
            )}

            {product.brand && (
              <span>
                {t("productDetail.brand")}: <strong>{product.brand}</strong>
              </span>
            )}
          </div>

          <p className="product-description">{product.description}</p>

          <strong className="product-price">
            {Number(product.price).toFixed(2)} €
          </strong>

          <p className={product.stock > 0 ? "stock-ok" : "stock-ko"}>
            {product.stock > 0
              ? t("productDetail.inStock")
              : t("productDetail.outOfStock")}
          </p>

          {product.stock > 0 && product.stock <= 5 && (
            <p className="stock-warning">
              {t("productDetail.lowStock")}
            </p>
          )}

          {product.stock > 0 && (
            <div className="quantity-selector">
              <label htmlFor="quantity">
                {t("productDetail.quantity") || "Cantidad"}
              </label>

              <input
                id="quantity"
                type="number"
                min="1"
                value={quantity}
                onChange={handleQuantityChange}
              />

              {quantityError && (
                <p className="quantity-error">{quantityError}</p>
              )}
            </div>
          )}

          <button
            disabled={product.stock <= 0}
            onClick={handleAddToCart}
          >
            {product.stock <= 0
              ? t("productDetail.outOfStock")
              : t("productDetail.addToCart")}
          </button>
        </div>
      </section>

      {product.specs?.length > 0 && (
        <section className="product-specs-section">
          <h2>{t("productDetail.specs")}</h2>

          <div className="product-specs-table">
            {product.specs.map((spec, index) => (
              <div className="product-spec-row" key={`${spec.name}-${index}`}>
                <span>{spec.name}</span>
                <strong>{spec.value}</strong>
              </div>
            ))}
          </div>
        </section>
      )}

      {product.compatibilities?.length > 0 && (
        <section className="product-compatibilities">
          <h2>{t("productDetail.compatibilitiesTitle")}</h2>

          <div className="compatibility-list">
            {product.compatibilities.map((item) => (
              <div
                className="compatibility-card"
                key={item.equipmentModelId}
              >
                <strong>
                  {item.brandName} {item.modelName}
                </strong>

                <span>{item.equipmentTypeName}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="product-reviews-section">
        <div className="product-reviews-header">
          <h2>{t("productDetail.reviews")}</h2>

          <p>
            ⭐ {Number(product.averageRating || 0).toFixed(1)} ·{" "}
            {product.reviewsCount || 0} {t("productDetail.reviewsCount")}
          </p>
        </div>

        {authenticated ? (
          <div className="product-review-form">
            <h3>{t("productDetail.writeReviewTitle")}</h3>

            {reviewError && <p className="review-error">{reviewError}</p>}
            {reviewSuccess && <p className="review-success">{reviewSuccess}</p>}

            <select
              value={rating}
              onChange={(e) => setRating(Number(e.target.value))}
            >
              <option value={5}>5 ⭐</option>
              <option value={4}>4 ⭐</option>
              <option value={3}>3 ⭐</option>
              <option value={2}>2 ⭐</option>
              <option value={1}>1 ⭐</option>
            </select>

            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t("productDetail.reviewPlaceholder")}
            />

            <button type="button" onClick={submitReview}>
              {t("productDetail.sendReview")}
            </button>
          </div>
        ) : (
          <p className="product-no-reviews">
            {t("productDetail.loginToReview")}
          </p>
        )}

        <div className="product-reviews-list">
          {reviews.length === 0 ? (
            <p className="product-no-reviews">
              {t("productDetail.noReviews")}
            </p>
          ) : (
            reviews.map((review) => (
              <article className="product-review-card" key={review.id}>
                <div className="product-review-card-header">
                  <strong>{review.customerName}</strong>
                  <span>{"⭐".repeat(review.rating)}</span>
                </div>

                {review.comment && <p>{review.comment}</p>}

                <small>
                  {new Date(review.createdAt).toLocaleDateString()}
                </small>
              </article>
            ))
          )}
        </div>
      </section>

      {relatedProducts.length > 0 && (
        <section className="related-products-section">
          <h2>{t("productDetail.relatedProducts")}</h2>

          <div className="related-products-grid">
            {relatedProducts.map((related) => (
              <article
                key={related.id}
                className="related-product-card"
                onClick={() => navigate(`/shop/${related.slug}`)}
              >
                {related.imageUrl && (
                  <img src={related.imageUrl} alt={related.name} />
                )}

                <h3>{related.name}</h3>

                <p>
                  ⭐ {Number(related.averageRating || 0).toFixed(1)} ·{" "}
                  {related.reviewsCount || 0}
                </p>

                <strong>{Number(related.price).toFixed(2)} €</strong>
              </article>
            ))}
          </div>
        </section>
      )}

      {lightboxOpen && (
        <div className="lightbox-overlay" onClick={() => setLightboxOpen(false)}>
            <button
            type="button"
            className="lightbox-close"
            onClick={() => setLightboxOpen(false)}
            >
            ×
            </button>

            <img
            className="lightbox-image"
            src={selectedImage}
            alt={product.name}
            onClick={(e) => e.stopPropagation()}
            />
        </div>
      )}
    </main>
  );
}